import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { GetInteractionAggregatesReq } from '@/api/generated/model';
import { mapAPIToPaginatedInteractionAggregates } from '@/models/interaction-aggregates/mappers/map-from-api';
import { useEnvironmentContext } from '@/providers/environment.provider';
import { QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES, QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { UseFetchParams } from '@/models/common.types';
import { useMemo } from 'react';
import { AggregateBy } from '@/domains/dashboard/components/GlobalfiltersRow';
import { mapAggregateVariables, mapAggregateOrdering } from '@/models/interaction-aggregates/utils';
import { addCustomAggregatePrefix, isCustomAggregate } from '../common/filters';
import { splitFilters } from '@/utils/filter-utils';
import { GetCustomCategoriesParams } from '@/api/generated/model/getCustomCategoriesParams';
import { getCustomCategories } from '@/api/generated/categories/categories';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import { getAggregatedData } from '@/api/generated/reports/reports';
export function useFetchInteractionAggregates(t0) {
  const $ = _c(31);
  const {
    payload,
    enabled,
    keepPreviousData,
    suspense,
    keys,
    deps,
    cacheTime,
    aggregateBy,
    onSuccess
  } = t0;
  const {
    enableLLMIssuesV2
  } = useEnvironmentContext();
  const aggregateByFinal = aggregateBy;
  const mappedPayload = useBuildPayloadInteractionAggregates(payload, aggregateByFinal);
  let t1;
  if ($[0] !== payload.project_id) {
    t1 = {
      payload: {
        project_id: payload.project_id
      }
    };
    $[0] = payload.project_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: customAggregateCategories,
    isLoading: isLoadingCustomAggregateCategories
  } = useFetchCustomAggregateCategories(t1);
  let t2;
  if ($[2] !== keys) {
    t2 = keys ?? [];
    $[2] = keys;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== aggregateByFinal || $[5] !== deps || $[6] !== enableLLMIssuesV2 || $[7] !== enabled || $[8] !== keepPreviousData || $[9] !== mappedPayload || $[10] !== suspense || $[11] !== t2) {
    t3 = [...t2, QUERY_KEY_INTERACTION_AGGREGATES, keepPreviousData, enabled, suspense, mappedPayload, enableLLMIssuesV2, aggregateByFinal, deps];
    $[4] = aggregateByFinal;
    $[5] = deps;
    $[6] = enableLLMIssuesV2;
    $[7] = enabled;
    $[8] = keepPreviousData;
    $[9] = mappedPayload;
    $[10] = suspense;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const queryKey = t3;
  let t4;
  if ($[13] !== mappedPayload) {
    t4 = t5 => {
      const {
        signal
      } = t5;
      return getInteractionAggregates(mappedPayload, {
        signal
      });
    };
    $[13] = mappedPayload;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = enabled && !isLoadingCustomAggregateCategories;
  let t6;
  if ($[15] !== aggregateByFinal || $[16] !== customAggregateCategories || $[17] !== enableLLMIssuesV2) {
    t6 = data => mapAPIToPaginatedInteractionAggregates(data, {
      enableLLMIssuesV2,
      aggregateBy: aggregateByFinal,
      customAggregateCategories: customAggregateCategories ?? []
    });
    $[15] = aggregateByFinal;
    $[16] = customAggregateCategories;
    $[17] = enableLLMIssuesV2;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== cacheTime || $[20] !== keepPreviousData || $[21] !== onSuccess || $[22] !== queryKey || $[23] !== suspense || $[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
    t7 = {
      queryKey,
      queryFn: t4,
      enabled: t5,
      select: t6,
      staleTime: cacheTime,
      cacheTime,
      keepPreviousData,
      suspense,
      onSuccess
    };
    $[19] = cacheTime;
    $[20] = keepPreviousData;
    $[21] = onSuccess;
    $[22] = queryKey;
    $[23] = suspense;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const query = useQuery(t7);
  const t8 = query.isLoading || isLoadingCustomAggregateCategories;
  let t9;
  if ($[28] !== query || $[29] !== t8) {
    t9 = {
      ...query,
      isLoading: t8
    };
    $[28] = query;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
export function useFetchAggregatedData(t0) {
  const $ = _c(31);
  const {
    payload,
    enabled,
    keepPreviousData,
    suspense,
    keys,
    deps,
    cacheTime,
    aggregateBy,
    onSuccess
  } = t0;
  const {
    enableLLMIssuesV2
  } = useEnvironmentContext();
  const aggregateByFinal = aggregateBy;
  const mappedPayload = useBuildPayloadInteractionAggregates(payload, aggregateByFinal);
  let t1;
  if ($[0] !== payload.project_id) {
    t1 = {
      payload: {
        project_id: payload.project_id
      }
    };
    $[0] = payload.project_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: customAggregateCategories,
    isLoading: isLoadingCustomAggregateCategories
  } = useFetchCustomAggregateCategories(t1);
  let t2;
  if ($[2] !== keys) {
    t2 = keys ?? [];
    $[2] = keys;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== aggregateByFinal || $[5] !== deps || $[6] !== enableLLMIssuesV2 || $[7] !== enabled || $[8] !== keepPreviousData || $[9] !== mappedPayload || $[10] !== suspense || $[11] !== t2) {
    t3 = [...t2, "aggregateData", keepPreviousData, enabled, suspense, mappedPayload, enableLLMIssuesV2, aggregateByFinal, deps];
    $[4] = aggregateByFinal;
    $[5] = deps;
    $[6] = enableLLMIssuesV2;
    $[7] = enabled;
    $[8] = keepPreviousData;
    $[9] = mappedPayload;
    $[10] = suspense;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const queryKey = t3;
  let t4;
  if ($[13] !== mappedPayload) {
    t4 = t5 => {
      const {
        signal
      } = t5;
      return getAggregatedData(mappedPayload.project_id, mappedPayload, {
        signal
      });
    };
    $[13] = mappedPayload;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = enabled && !isLoadingCustomAggregateCategories;
  let t6;
  if ($[15] !== aggregateByFinal || $[16] !== customAggregateCategories || $[17] !== enableLLMIssuesV2) {
    t6 = data => mapAPIToPaginatedInteractionAggregates(data, {
      enableLLMIssuesV2,
      aggregateBy: aggregateByFinal,
      customAggregateCategories: customAggregateCategories ?? []
    });
    $[15] = aggregateByFinal;
    $[16] = customAggregateCategories;
    $[17] = enableLLMIssuesV2;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== cacheTime || $[20] !== keepPreviousData || $[21] !== onSuccess || $[22] !== queryKey || $[23] !== suspense || $[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
    t7 = {
      queryKey,
      queryFn: t4,
      enabled: t5,
      select: t6,
      staleTime: cacheTime,
      cacheTime,
      keepPreviousData,
      suspense,
      onSuccess
    };
    $[19] = cacheTime;
    $[20] = keepPreviousData;
    $[21] = onSuccess;
    $[22] = queryKey;
    $[23] = suspense;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const query = useQuery(t7);
  const t8 = query.isLoading || isLoadingCustomAggregateCategories;
  let t9;
  if ($[28] !== query || $[29] !== t8) {
    t9 = {
      ...query,
      isLoading: t8
    };
    $[28] = query;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
export function useFetchCustomAggregateCategories(t0) {
  const $ = _c(7);
  const {
    payload
  } = t0;
  let t1;
  let t2;
  if ($[0] !== payload) {
    t1 = [QUERY_KEY_FETCH_CUSTOM_AGGREGATE_CATEGORIES, payload];
    t2 = () => getCustomCategories(payload);
    $[0] = payload;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!payload.project_id;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      cacheTime: 0,
      select: _temp
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t4);
}

// function mapGroupByToCustomCategoryKind(
//   groupBy: GetInteractionAggregatesReq['group_by'],
// ): ListCustomAggregateCategories['kind'] {
//   if (groupBy?.kind === 'topic') return 'topic';
//   if (groupBy?.kind === 'type_of_problem_v2') return 'model_classes';
//
//   return undefined;
// }
function _temp(data) {
  return data.categories;
}
export function useBuildPayloadInteractionAggregates(payload: GetInteractionAggregatesReq, aggregateBy: AggregateBy): GetInteractionAggregatesReq {
  const {
    regularFilters
  } = splitFilters(payload.filters);
  return useMemo(() => {
    return {
      ...payload,
      order_by: payload.order_by ? mapAggregateOrdering(payload.order_by, {
        aggregateBy
      }) : undefined,
      filters: mapFilters(regularFilters, aggregateBy),
      variables: mapAggregateVariables(payload.variables ?? [], {
        aggregateBy
      })
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(payload), aggregateBy, regularFilters]);
}
function mapFilters(filters: GetInteractionAggregatesReq['filters'], aggregateBy: AggregateBy): GetInteractionAggregatesReq['filters'] {
  if (!filters) return filters;
  return filters.map(filter => {
    if (isCustomAggregate(filter.column)) {
      return {
        ...filter,
        column: addCustomAggregatePrefix(filter.column, aggregateBy)
      };
    }
    return filter;
  });
}